import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import GetAuthContext from "../../../../src/shared/context/AuthContext";
import ActivityTimeline from "../../components/atomic/ActivityTimeline";
import type { DropdownWithSearchOption } from "../../components/atomic/DropdownWithSearch/interface";
import ApplicantSelectionDropdown from "../../components/composed/ApplicantSelectionDropdown";
import { LoanApplicationService } from "../../services/LoanApplication";
import { notify } from "../../shared/context/AlertContext";
import type { CustomLoaderFunctionArgs } from "../../shared/router/RouteLoader";
import { LENDER_IDs, whiteLabelStyling } from "../../shared/utils/constants";
import { arrangeDirectors } from "../Directors";
import { userActiveStruct } from "../Directors/helper";

export async function loanTimelineLoader({
  getToken,
  params,
  pageName
}: CustomLoaderFunctionArgs) {
  const { loanID } = params;

  return await LoanApplicationService.getLoanActivity({
    loanApplicationID: loanID!,
    token: getToken(),
    pageName: pageName!,
    isLenderDashboard: false,
    page: 1
  });
}

function LoanTimelineTab({
  pageName,
  data,
  loanDetails,
  loanID,
  token,
  isLenderPlatform,
  isMFL_LAP
}: {
  token: string;
  pageName?: string;
  data: any;
  loanDetails: any;
  loanID: any;
  isLenderPlatform: boolean;
  isMFL_LAP: boolean;
}) {
  const { whiteLabelStyle } = whiteLabelStyling();
  const { userState } = GetAuthContext();
  const isABFL_CO_LENDING =
    (userState?.userData as any)?.lenderID === LENDER_IDs.ABFL_CO_LENDING;
  const { userActivityHistory } = data || {};

  const [page, setPage] = useState(1);
  const [loanActivity, setLoanActivity] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);

  const isDirectorOperation = ["Private Limited", "Partnership"].includes(
    loanDetails?.constitution
  );
  const optionsDetails = isDirectorOperation
    ? arrangeDirectors(loanDetails?.directorDetails) || []
    : (loanDetails?.coApplicantDetails ?? []);

  const optionList: DropdownWithSearchOption[] = useMemo(
    () => [
      {
        name: isDirectorOperation
          ? userActiveStruct({
              name: loanDetails?.name,
              active: loanDetails?.active
            })
          : "Primary Applicant",
        value: loanDetails.userID
      },
      ...optionsDetails?.map((dir: any, idx: number) => ({
        name: isDirectorOperation
          ? userActiveStruct({
              name: dir?.name,
              active: dir?.active
            })
          : "Co-Applicant " + (idx + 1),
        value: dir.userID
      }))
    ],
    [loanDetails]
  );

  const [selectedDirector, setSelectedDirector] =
    useState<DropdownWithSearchOption>(optionList?.[0]);

  useEffect(() => {
    setLoanActivity(userActivityHistory || []);
  }, [userActivityHistory]);

  async function getLoanActivity(page = 1, userID: string) {
    const response: any = await LoanApplicationService.getLoanActivity({
      loanApplicationID: loanID,
      userID,
      token,
      pageName: pageName!,
      isLenderDashboard: isLenderPlatform,
      page
    });

    if (response.status) {
      const newActivities = response.data?.userActivityHistory || [];

      if (newActivities.length > 0) {
        setLoanActivity((prevActivities) => [
          ...prevActivities,
          ...newActivities
        ]);
      } else setHasMorePages(false);
    } else {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
    }
  }

  useEffect(() => {
    if (page > 1 && hasMorePages) {
      getLoanActivity(page, selectedDirector?.value);
    }
  }, [page, hasMorePages]);

  function onViewMoreClick() {
    setPage((prevPage) => prevPage + 1);
  }

  const onChangeDirector = (value: DropdownWithSearchOption) => {
    setPage(1);
    setLoanActivity([]);
    setHasMorePages(true);
    setSelectedDirector(value);
    getLoanActivity(1, value?.value);
  };

  const placeholderDir = isDirectorOperation
    ? loanDetails?.constitution === "Partnership"
      ? "Partner"
      : "Director"
    : "Co-Applicant";

  return (
    <div className="px-4 mt-4 overflow-auto">
      <div className="flex justify-between items-center text-base font-medium">
        <h3
          className={classNames(
            "text-sm text-gray-700 font-medium w-max border-b-2 py-2 pr-2",
            whiteLabelStyle?.theme?.color?.border || "border-indigo-500"
          )}
        >
          Timeline
        </h3>

        {optionList?.length > 1 && !isABFL_CO_LENDING && (
          <ApplicantSelectionDropdown
            loanDetails={loanDetails}
            truncateName={false}
            styles={{ container: "!w-96" }}
            onChange={(applicant: DropdownWithSearchOption) =>
              onChangeDirector(applicant)
            }
            selectedApplicant={selectedDirector as any}
            setSelectedApplicant={setSelectedDirector}
            setApplicantData={() => {}}
            resetApplicantState={false}
            hasSearch={false}
            placeholder={`Select ${placeholderDir}`}
          />
        )}
      </div>

      <div className="min-h-[50vh]">
        <ActivityTimeline
          page={page}
          timeline={loanActivity}
          hasMorePages={hasMorePages}
          onViewMoreClick={onViewMoreClick}
          whiteLabelStyle={whiteLabelStyle}
        />
      </div>
    </div>
  );
}

export default LoanTimelineTab;
